import {
    EventClient,
    EventClientProvider,
    FormattedEvent,
    FormattedEventResponse,
} from '@eb/event-renderer';
import React, { useEffect, useState } from 'react';
import { AppProps } from '../../types';
import {
    getShareComponentProps,
    ShareProps,
} from '../ShareEvent/ShareEventRoot';
import { dynaMount } from '../utils/mountComponent';
import {
    getOrganizerRelatedEventsFromDestination,
    getRelatedEvents,
} from './api';
import MoreEventsFromThisOrganizer from './MoreEventsFromThisOrganizer';
import OtherEventsYouMayLike from './OtherEventsYouMayLike';
import { useExpiredEvents } from './useExpiredEvents';

const eventClient = new EventClient();

export interface RelatedEventsProps {
    eventId: string;
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
    events?: FormattedEvent[];
    organizerId: string;
}

export type RelatedEventsChildrenProps = Omit<
    RelatedEventsProps,
    'organizerId' | 'eventId'
>;

export const RELATED_EVENTS_ID = 'listings-root__related-events';
export const MORE_EVENTS_FROM_THIS_ORGANIZER_ID =
    'listings-root__more-events-from-this-organizer';

export const getRelatedEventsProps = (props: AppProps): RelatedEventsProps => {
    const {
        event: { id: eventId },
        user: { isAuthenticated },
        organizer: { id: organizerId },
    } = props;

    const shareComponentProps = getShareComponentProps(props);

    return {
        eventId,
        isAuthenticated,
        shareComponentProps,
        organizerId,
    };
};

export const OtherEventsYouMayLikeMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: OtherEventsYouMayLike,
            containerId: RELATED_EVENTS_ID,
        },
        { ...props },
    );

export const MoreEventsFromThisOrganizerMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: MoreEventsFromThisOrganizer,
            containerId: MORE_EVENTS_FROM_THIS_ORGANIZER_ID,
        },
        { ...props },
    );

export const RelatedEventsWrapper = (
    props: RelatedEventsProps,
): JSX.Element | null => {
    const [relatedEvents, setRelatedEvents] = useState<FormattedEvent[]>([]);
    const [
        relatedEventsSameOrganizer,
        setRelatedEventsSameOrganizer,
    ] = useState<FormattedEvent[]>([]);
    const { organizerId, eventId } = props;

    useEffect(() => {
        // TODO: We should replace this part to use
        // should_show_related_events_live prop from the mako template instead of a DOM element
        // https://github.com/eventbrite/core/blob/master/django/templates/event/event_page/listing/_listing_footer_related_events.html#L1
        const shouldShowRelatedEventsLive = document.getElementById(
            RELATED_EVENTS_ID,
        );

        getOrganizerRelatedEventsFromDestination(organizerId, eventId).then(
            (response: FormattedEventResponse | undefined) => {
                let organizerEvents: string[] = [];
                if (response) {
                    organizerEvents = response.events.map((event) => event.id);
                    setRelatedEventsSameOrganizer(response.events);
                }
                if (shouldShowRelatedEventsLive) {
                    getRelatedEvents(eventId).then((dataFromApi) => {
                        const filteredEvents =
                            organizerEvents.length > 0
                                ? dataFromApi.events.filter(
                                      (event) =>
                                          !organizerEvents.includes(event.id),
                                  )
                                : dataFromApi.events;
                        if (filteredEvents.length > 0) {
                            setRelatedEvents(filteredEvents);
                        }
                    });
                }
            },
        );
    }, [eventId, organizerId]);

    useExpiredEvents();

    return (
        <EventClientProvider client={eventClient}>
            <div data-testid="related-events-wrapper">
                {relatedEventsSameOrganizer.length > 0 && (
                    <MoreEventsFromThisOrganizerMount
                        {...props}
                        events={relatedEventsSameOrganizer}
                    />
                )}

                {relatedEvents.length > 0 && (
                    <OtherEventsYouMayLikeMount
                        {...props}
                        events={relatedEvents}
                    />
                )}
            </div>
        </EventClientProvider>
    );
};

const RelatedEventsRoot = (props: AppProps): JSX.Element | null => {
    // TODO: We should replace this part to use
    // enable_more_events_from_this_organizer_on_listings FF from the mako template
    // https://github.com/eventbrite/core/blob/master/django/templates/event/event_page/listing/_event_listing_body.html#L30
    const enableMoreEventsFromThisOrganizer = document.getElementById(
        MORE_EVENTS_FROM_THIS_ORGANIZER_ID,
    );

    if (!enableMoreEventsFromThisOrganizer) {
        return <div data-testid="loading-state"></div>;
    }
    const relatedEventsProps = getRelatedEventsProps(props);
    return <RelatedEventsWrapper {...relatedEventsProps} />;
};

export default RelatedEventsRoot;
